import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell, isEmbeddedInAtlassian } from '@trello/browser';
import { mergeRefs } from '@trello/dom-hooks';
import { DynamicButton } from '@trello/dynamic-tokens';
import { forTemplate } from '@trello/legacy-i18n';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, PopoverScreen, usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import type { BoardHeaderTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useIsBoardHeaderAndMenuRedesignEnabled } from 'app/src/components/BoardHeader/useIsBoardHeaderAndMenuRedesignEnabled';
import { MaybePremiumBanner } from 'app/src/components/FreeTrial';
import { LazyArrangeViewButtonsPopoverScreen } from './LazyArrangeViewButtonsPopoverScreen';
import type {
  SwitcherView,
  SwitcherViewViewType,
  ViewOption,
} from './ViewSwitcher';
import { ViewSwitcherButton } from './ViewSwitcherButton';

// eslint-disable-next-line @trello/less-matches-component
import styles from './ViewSwitcher.less';

const format = forTemplate('view_switcher');

interface ViewSwitcherButtonProps {
  idBoard: string;
  currentView: SwitcherViewViewType | null;
  viewOptions: { [key in SwitcherViewViewType]: ViewOption };
  switcherViews: SwitcherView[];
  hasViewsFeature: boolean;
  memberCanEditBoard: boolean;
  analyticsContainers?: Parameters<
    typeof Analytics.sendClickedButtonEvent
  >[0]['containers'];
  isCollapsed?: boolean;
}

const emptyObj = {};
export const ViewSwitcherButtons: FunctionComponent<
  ViewSwitcherButtonProps
> = ({
  idBoard,
  currentView,
  viewOptions,
  switcherViews,
  hasViewsFeature,
  memberCanEditBoard,
  analyticsContainers = emptyObj,
  isCollapsed,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const { headerRedesignEnabled } = useIsBoardHeaderAndMenuRedesignEnabled();

  const onShow = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardViewSwitcherMoreButton',
      source: 'boardScreen',
      containers: analyticsContainers,
    });
    setIsPopoverOpen(true);
  }, [analyticsContainers]);

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { triggerRef, hide, toggle, popoverProps } =
    usePopover<HTMLButtonElement>({
      onShow,
    });

  // Because the popover is anchored to a dummy div instead of the button, this
  // ensures that closing out of the popover returns focus to the button.
  const onPopoverHide = useCallback(() => {
    hide();
    setIsPopoverOpen(false);
  }, [hide]);

  const onToggle = useCallback(() => {
    toggle();
  }, [toggle]);

  useEffect(() => {
    if (popoverProps?.isVisible) {
      // if the popover is already open, don't change the state
      return;
    }
  }, [popoverProps?.isVisible]);

  // The buttonRef is used to persist the location the popover was opened from,
  // the triggerRef is supplied from the popover to make sure clicking the
  // button isn't registered as an outsideClick.
  const combinedButtonRefs = mergeRefs(buttonRef, triggerRef);

  const enabledViews = switcherViews.filter(({ enabled }) => enabled);

  const switcherViewButtons = switcherViews.map(({ viewType, enabled }) => {
    if (!enabled) return null;
    if (isEmbeddedInAtlassian() && viewType !== currentView) return null;

    return (
      <ViewSwitcherButton
        key={viewType}
        idBoard={idBoard}
        analyticsContainers={analyticsContainers}
        isCollapsed={isCollapsed === true && enabledViews.length > 1}
        currentView={currentView}
        viewOption={viewOptions[viewType]}
        viewType={viewType}
      />
    );
  });

  const collapsedButtonText = format(currentView || 'Board');

  const ViewIcon = viewOptions[currentView || 'Board'].icon;

  const popoverTitle = useMemo(() => {
    if (hasViewsFeature) {
      return memberCanEditBoard && !headerRedesignEnabled
        ? format('add-and-edit-views')
        : format('views');
    }
    return dontUpsell() ? format('views') : format('upgrade-for-views');
  }, [hasViewsFeature, memberCanEditBoard, headerRedesignEnabled]);

  const premiumTrialFormat = forTemplate('premium_trial');

  return (
    <>
      <div className={styles.switcherButtonsContainer}>
        {!headerRedesignEnabled && switcherViewButtons}
        <Tooltip
          content={format(headerRedesignEnabled ? 'views' : 'customize-views')}
        >
          <DynamicButton
            className={cx({
              [styles.overflowButton]: true,
              [styles.highlighted]: isPopoverOpen,
              [styles.overflowButtonLarge]: headerRedesignEnabled,
            })}
            aria-label={format(
              headerRedesignEnabled ? 'views' : 'customize-views',
            )}
            iconBefore={
              <ViewIcon
                size={headerRedesignEnabled ? 'medium' : 'small'}
                dangerous_className={cx({
                  [styles.viewSwitcherIcon]: true,
                  [styles.hideOnNormalScreens]: !headerRedesignEnabled,
                })}
              />
            }
            iconAfter={
              <DownIcon
                size={headerRedesignEnabled ? 'small' : 'medium'}
                dangerous_className={styles.downIcon}
              />
            }
            ref={combinedButtonRefs}
            onClick={onToggle}
            data-testid={getTestId<BoardHeaderTestIds>(
              'view-switcher-button-more',
            )}
          >
            {!headerRedesignEnabled && (
              <span className={styles.hideOnNormalScreens}>
                {collapsedButtonText}
              </span>
            )}
          </DynamicButton>
        </Tooltip>
      </div>
      <Popover {...popoverProps} onHide={onPopoverHide} title={popoverTitle}>
        <PopoverScreen id={0}>
          <MaybePremiumBanner
            title={premiumTrialFormat('views-title')}
            description={premiumTrialFormat('views-description')}
            className={styles.premiumBanner}
          />
          <LazyArrangeViewButtonsPopoverScreen
            idBoard={idBoard}
            viewOptions={viewOptions}
            switcherViews={switcherViews}
            hasViewsFeature={hasViewsFeature}
            memberCanEditBoard={memberCanEditBoard}
            analyticsContainers={analyticsContainers}
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
